import React from 'react';
import { useDispatch } from 'react-redux';
import { type UserInfo } from '@/auth/useUserInfo';
import { ResourceNames } from '@/lib/constants';
import { useShallowSelector } from '@/lib/reduxHooks';
import * as actions from '../../store/senderBuild/actions';

export function useFetchEnvelopeInfoAndRecipients(args: {
  envelopeId: string | null;
  setDefaultZoom: () => void;
  resource: ResourceNames;
  userInfo: UserInfo | null;
}) {
  const { envelopeId, setDefaultZoom, resource, userInfo } = args;

  const dispatch = useDispatch();
  const state = useShallowSelector((state) => ({
    envelopeId: state.senderBuild.id,
    resource: state.senderBuild.resource,
  }));

  const isFetchingNewEnvelopeRef = React.useRef<boolean | null>(null);

  const setDefaultZoomRef = React.useRef(setDefaultZoom);
  setDefaultZoomRef.current = setDefaultZoom;

  //
  // FETCH ENVELOPE INFO
  //
  React.useEffect(() => {
    if (state.envelopeId || !userInfo?.provisioned || !envelopeId || isFetchingNewEnvelopeRef.current != null) return;

    // userInfo has been fetched, but envelope info hasn't been fetched yet
    isFetchingNewEnvelopeRef.current = true;
    dispatch(actions.fetch(envelopeId, resource));
    setDefaultZoomRef.current?.();
  }, [dispatch, envelopeId, resource, state.envelopeId, userInfo]);

  //
  // FETCH RECIPIENTS AFTER ENVELOPE INFO IS FETCHED
  //
  React.useEffect(() => {
    if (isFetchingNewEnvelopeRef.current && state.envelopeId && state.resource === ResourceNames.ENVELOPE) {
      // Envelope info has been fetched; now fetch recipients
      dispatch(actions.fetchRecipients(state.envelopeId));
      isFetchingNewEnvelopeRef.current = false;
    }
  }, [dispatch, state.envelopeId, state.resource]);
}
